import React from "react";
import { Crown } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { AnalysisOptions } from "../types";

interface JobDescriptionFormProps {
  jobDescription: string;
  onJobDescriptionChange: (value: string) => void;
  analysisOptions: AnalysisOptions;
  onAnalyze: () => void;
  isAnalysisReady: boolean;
  isAnalysisInProgress: boolean;
  analyzeButtonText: string;
  onUpgradeClick: () => void;
  showUpgradePrompt: boolean;
}

const JobDescriptionForm: React.FC<JobDescriptionFormProps> = ({
  jobDescription,
  onJobDescriptionChange,
  analysisOptions,
  onAnalyze,
  isAnalysisReady,
  isAnalysisInProgress,
  analyzeButtonText,
  onUpgradeClick,
  showUpgradePrompt,
}) => {
  const { t } = useTranslation();

  const handleAnalyzeClick = () => {
    if (showUpgradePrompt) {
      onUpgradeClick();
    } else {
      onAnalyze();
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        {/* Description du poste */}
        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <label className="block font-semibold text-gray-700 mb-3">
            {t("job_description.title")}
          </label>
          
          <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-200">
            <div className="flex items-start">
              <div className="text-blue-500 mr-3 mt-1">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">{t("job_description.important_instructions")}</h4>
                <p className="text-sm text-blue-800">
                  {t("job_description.copy_paste_instructions")}
                </p>
              </div>
            </div>
          </div>
          
          <textarea
            value={jobDescription}
            onChange={(e) => onJobDescriptionChange(e.target.value)}
            className="w-full min-h-64 p-4 border-2 border-gray-200 rounded-lg font-base 
                      resize-y focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            placeholder={t("job_description.placeholder")}
          ></textarea>
        </div>
        
        {/* Options d'analyse */}
        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <label className="block font-semibold text-gray-700 mb-3">
            {t("job_description.analysis_parameters")}
          </label>
          
          <div className="bg-green-50 rounded-lg p-3 mb-4 border border-green-200">
            <div className="flex items-center">
              <div className="text-green-600 mr-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-sm text-green-800 font-medium">
                {t("job_description.analysis_complete_active")}
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
              <input
                type="checkbox"
                id="analyzeSkills"
                name="analyzeSkills"
                checked={analysisOptions.analyzeSkills}
                onChange={() => {}} // Empêcher le changement
                disabled={true}
                className="w-5 h-5 text-green-600 rounded mr-3 cursor-not-allowed"
              />
              <label htmlFor="analyzeSkills" className="cursor-not-allowed text-green-800 font-medium">
                {t("job_description.technical_skills")}
              </label>
            </div>
            <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
              <input
                type="checkbox"
                id="analyzeExperience"
                name="analyzeExperience"
                checked={analysisOptions.analyzeExperience}
                onChange={() => {}} // Empêcher le changement
                disabled={true}
                className="w-5 h-5 text-green-600 rounded mr-3 cursor-not-allowed"
              />
              <label htmlFor="analyzeExperience" className="cursor-not-allowed text-green-800 font-medium">
                {t("job_description.professional_experience")}
              </label>
            </div>
            <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
              <input
                type="checkbox"
                id="analyzeEducation"
                name="analyzeEducation"
                checked={analysisOptions.analyzeEducation}
                onChange={() => {}} // Empêcher le changement
                disabled={true}
                className="w-5 h-5 text-green-600 rounded mr-3 cursor-not-allowed"
              />
              <label htmlFor="analyzeEducation" className="cursor-not-allowed text-green-800 font-medium">
                {t("job_description.education_diplomas")}
              </label>
            </div>
            <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
              <input
                type="checkbox"
                id="analyzeCertifications"
                name="analyzeCertifications"
                checked={analysisOptions.analyzeCertifications}
                onChange={() => {}} // Empêcher le changement
                disabled={true}
                className="w-5 h-5 text-green-600 rounded mr-3 cursor-not-allowed"
              />
              <label htmlFor="analyzeCertifications" className="cursor-not-allowed text-green-800 font-medium">
                {t("job_description.certifications")}
              </label>
            </div>
            <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
              <input
                type="checkbox"
                id="analyzeLanguages"
                name="analyzeLanguages"
                checked={analysisOptions.analyzeLanguages}
                onChange={() => {}} // Empêcher le changement
                disabled={true}
                className="w-5 h-5 text-green-600 rounded mr-3 cursor-not-allowed"
              />
              <label htmlFor="analyzeLanguages" className="cursor-not-allowed text-green-800 font-medium">
                {t("job_description.languages")}
              </label>
            </div>
            <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
              <input
                type="checkbox"
                id="generateRecommendations"
                name="generateRecommendations"
                checked={analysisOptions.generateRecommendations}
                onChange={() => {}} // Empêcher le changement
                disabled={true}
                className="w-5 h-5 text-green-600 rounded mr-3 cursor-not-allowed"
              />
              <label htmlFor="generateRecommendations" className="cursor-not-allowed text-green-800 font-medium">
                {t("job_description.recommendations")}
              </label>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={handleAnalyzeClick}
        disabled={!isAnalysisReady && !showUpgradePrompt && !isAnalysisInProgress}
        className={`w-full py-4 px-6 rounded-xl text-xl font-semibold mb-8 transition-all transform hover:-translate-y-1 ${
          showUpgradePrompt
            ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg hover:shadow-xl'
            : isAnalysisReady && !isAnalysisInProgress
            ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg hover:shadow-xl'
            : 'bg-gray-300 text-gray-600 cursor-not-allowed'
        }`}
      >
        {showUpgradePrompt ? (
          <div className="flex items-center justify-center">
            <Crown className="w-6 h-6 mr-2" />
            {t('job_description.upgrade_to_continue')}
          </div>
        ) : (
          analyzeButtonText
        )}
      </button>

      {showUpgradePrompt && (
        <div className="mb-8 p-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-start">
            <Crown className="w-5 h-5 text-purple-500 mr-2 mt-0.5" />
            <div>
              <h4 className="font-semibold text-purple-900 mb-1">{t('job_description.quota_exhausted_title')}</h4>
              <p className="text-sm text-purple-800">
                {t('job_description.quota_exhausted_message')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDescriptionForm;
